import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { Modal } from '../components/Modal';
import { ImportExport, type ImportColumn } from '../components/ImportExport';
import { RecordInfoButton } from '../components/RecordInfoButton';
import { useToast } from '../components/Toast';
import type { BatchYear, ExamPeriodDto } from '../types/models';

const emptyForm = { examName: '', batchYearId: '', fromDate: '', toDate: '' };

// Excel upload columns. One row = one subject scheduled for every batch matching its
// Department/Program code(s) within the batch year; the exam name + batch year group the rows
// into an exam period. Department/Program codes may list several (comma-separated).
const SCHEDULE_COLUMNS: ImportColumn[] = [
  { header: 'Exam Name', key: 'examName', sample: 'Internal Exam II' },
  { header: 'Batch Year', key: 'batchYear', sample: '2024-2028' },
  { header: 'From Date', key: 'fromDate', sample: '2026-08-05' },
  { header: 'To Date', key: 'toDate', sample: '2026-08-12' },
  { header: 'Subject Code', key: 'subjectCode', sample: 'CS23401' },
  { header: 'Subject Name', key: 'subjectName', sample: 'Database Management Systems' },
  { header: 'Department Code', key: 'departmentCode', sample: 'CSE' },
  { header: 'Program Code', key: 'programCode', sample: 'BE-CSE' },
  { header: 'Window Opens', key: 'windowOpens', sample: '2026-08-05 09:30' },
  { header: 'Window Closes', key: 'windowCloses', sample: '2026-08-05 12:30' },
  { header: 'Duration', key: 'duration', sample: '180' },
  { header: 'Invigilator', key: 'invigilator', sample: 'Ramesh Kumar' },
  { header: 'Hall No', key: 'room', sample: 'Room 204' },
];

/** A datetime cell (local) → ISO-8601 UTC, matching the manual form (which does the same in the
 *  browser). Left as-is if unparseable, so the backend can report a clear error. */
function toIsoUtc(local: string): string {
  const s = (local ?? '').trim();
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toISOString();
}

/** A date cell → `YYYY-MM-DD` (drops any time part), left as-is if unparseable. */
function toDateOnly(v: string): string {
  const s = (v ?? '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** "2024-06-01" -> "01-06-2024" for display. */
function fmtDate(iso: string) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}-${m}-${y}` : iso;
}

function periodStatus(p: ExamPeriodDto): { label: string; bg: string; fg: string } {
  if (p.subjectCount === 0) return { label: 'No subjects', bg: 'var(--border)', fg: 'var(--muted)' };
  if (p.publishedCount === 0) return { label: 'Draft', bg: 'var(--border)', fg: 'var(--muted)' };
  if (p.publishedCount < p.subjectCount)
    return { label: `Partly published (${p.publishedCount}/${p.subjectCount})`, bg: 'var(--accent-soft-bg)', fg: 'var(--accent)' };
  return { label: 'Published', bg: 'var(--success-soft-bg)', fg: 'var(--success-soft-fg)' };
}

export function ExamSchedulePage() {
  const { orgId = '' } = useParams();
  const nav = useNavigate();
  const toast = useToast();

  const [periods, setPeriods] = useState<ExamPeriodDto[] | null>(null);
  const [batchYears, setBatchYears] = useState<BatchYear[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [fBatchYear, setFBatchYear] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  // When editing an exam that already has subjects, its batch year is fixed — changing it would
  // orphan those subjects (they belong to batches of the original batch year).
  const [lockBatchYear, setLockBatchYear] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [orgId]);

  async function refresh() {
    try {
      const [p, by] = await Promise.all([api.listExamPeriods(orgId), api.listBatchYears(orgId)]);
      setPeriods(p); setBatchYears(by);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load exam schedule.');
    }
  }

  // Normalise the date/time columns (window times → UTC, dates → YYYY-MM-DD) before upload.
  function importRows(rows: Record<string, string>[]) {
    const normalised = rows.map(r => ({
      ...r,
      fromDate: toDateOnly(r.fromDate),
      toDate: toDateOnly(r.toDate),
      windowOpens: toIsoUtc(r.windowOpens),
      windowCloses: toIsoUtc(r.windowCloses),
    }));
    return api.importExamSchedules(orgId, normalised);
  }

  const batchYearFilterOptions = useMemo(() => {
    const m = new Map<string, string>();
    (periods ?? []).forEach(p => m.set(p.batchYearId, p.batchYearName));
    return [...m].map(([id, label]) => ({ id, label }));
  }, [periods]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (periods ?? []).filter(p => {
      if (fBatchYear && p.batchYearId !== fBatchYear) return false;
      if (q && !p.examName.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [periods, search, fBatchYear]);

  function setField(key: keyof typeof emptyForm, value: string) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function openCreate() {
    setFormError(null); setEditId(null); setLockBatchYear(false); setForm({ ...emptyForm });
    setShowModal(true);
  }
  function openEdit(p: ExamPeriodDto) {
    setFormError(null); setEditId(p.id); setLockBatchYear(p.subjectCount > 0);
    setForm({ examName: p.examName, batchYearId: p.batchYearId, fromDate: p.fromDate, toDate: p.toDate });
    setShowModal(true);
  }
  function openPeriod(p: ExamPeriodDto) {
    nav(`/org/${orgId}/exam-schedule/period?id=${p.id}`);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!form.batchYearId) { setFormError('Select a batch year.'); return; }
    if (form.toDate < form.fromDate) { setFormError('To date must be on or after the From date.'); return; }
    const payload = {
      examName: form.examName.trim(), batchYearId: form.batchYearId,
      fromDate: form.fromDate, toDate: form.toDate,
    };
    setBusy(true);
    try {
      if (editId) {
        await api.updateExamPeriod(editId, payload);
        toast.success('Exam updated.');
        setShowModal(false);
        await refresh();
      } else {
        const created = await api.createExamPeriod(payload);
        toast.success('Exam created — now add subjects.');
        setShowModal(false);
        nav(`/org/${orgId}/exam-schedule/period?id=${created.id}`);
      }
    } catch (e: any) {
      setFormError(e?.message ?? 'Failed to save exam.');
    } finally { setBusy(false); }
  }

  async function remove(p: ExamPeriodDto) {
    if (!confirm(`Delete “${p.examName}” for ${p.batchYearName}? This also removes its ${p.subjectCount} subject(s) and cannot be undone.`)) return;
    try {
      await api.deleteExamPeriod(p.id);
      await refresh();
      toast.success('Exam deleted.');
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to delete.');
    }
  }

  return (
    <OrgLayout>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>Exam Schedule</h1>
        <button className="primary" onClick={openCreate} disabled={batchYears.length === 0}>+ Schedule exam</button>
      </div>
      <div className="muted" style={{ marginTop: 4 }}>
        Create an exam for a batch year with a date range, then add its subjects inside — or upload
        an Excel to bulk-create exams and their subjects at once.
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <div className="muted" style={{ marginBottom: 8, fontSize: 13 }}>
          Bulk upload — download the format, fill one row per subject (Department/Program codes may
          list several, comma-separated), then upload. Rows are grouped into exams by Exam Name + Batch Year.
        </div>
        <ImportExport
          columns={SCHEDULE_COLUMNS}
          templateName="exam-schedule-sample.xlsx"
          onImport={importRows}
          onDone={refresh}
        />
      </div>

      {batchYears.length === 0 && periods && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="muted">
            Add a <Link to={`/org/${orgId}/batch-years`}>batch year</Link> first — exams are scheduled for a batch year.
          </div>
        </div>
      )}

      {error && <div className="error" style={{ margin: '12px 0' }}>{error}</div>}

      <div className="card" style={{ marginTop: 16 }}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '2 1 240px' }}>
            <div className="muted">Search</div>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search exam name…" />
          </label>
          <label style={{ flex: '1 1 180px' }}>
            <div className="muted">Batch Year</div>
            <select value={fBatchYear} onChange={e => setFBatchYear(e.target.value)}>
              <option value="">All batch years</option>
              {batchYearFilterOptions.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
            </select>
          </label>
          {(search || fBatchYear) && (
            <button onClick={() => { setSearch(''); setFBatchYear(''); }}>Clear</button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Exam</th>
              <th>Batch Year</th>
              <th>Dates</th>
              <th>Subjects</th>
              <th>Status</th>
              <th style={{ width: 220 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!periods && <tr><td colSpan={6} className="muted">Loading…</td></tr>}
            {periods && filtered.length === 0 && (
              <tr><td colSpan={6} className="muted">
                {periods.length === 0 ? 'No exams scheduled yet.' : 'No exams match the filters.'}
              </td></tr>
            )}
            {filtered.map(p => {
              const st = periodStatus(p);
              return (
                <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => openPeriod(p)}>
                  <td>
                    <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {p.examName}
                      <span aria-hidden style={{ opacity: 0.5, fontSize: 12 }}>›</span>
                    </div>
                  </td>
                  <td><span className="batch-chip">{p.batchYearName}</span></td>
                  <td>{fmtDate(p.fromDate)} → {fmtDate(p.toDate)}</td>
                  <td>{p.subjectCount}</td>
                  <td>
                    <span style={{
                      display: 'inline-block', padding: '3px 11px', borderRadius: 999,
                      fontSize: 12, fontWeight: 600, background: st.bg, color: st.fg,
                    }}>● {st.label}</span>
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <button className="link-btn" onClick={() => openPeriod(p)}>Open</button>
                    <button className="link-btn" onClick={() => openEdit(p)}>Edit</button>
                    <button className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => remove(p)}>Delete</button>
                    <RecordInfoButton entity="exam-periods" id={p.id} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title={editId ? 'Edit exam' : 'Schedule exam'} onClose={() => setShowModal(false)} width={560}>
          <form onSubmit={submit} className="col" style={{ marginTop: 16 }}>
            <label>
              <div className="muted">Exam name / type *</div>
              <input value={form.examName} required maxLength={200} autoFocus
                     onChange={e => setField('examName', e.target.value)} placeholder="Internal Exam II" />
            </label>
            <label>
              <div className="muted">Batch Year *</div>
              <select value={form.batchYearId} required disabled={lockBatchYear}
                      onChange={e => setField('batchYearId', e.target.value)}>
                <option value="" disabled>
                  {batchYears.length ? 'Select batch year…' : 'No batch years — add them first'}
                </option>
                {batchYears.map(y => <option key={y.id} value={y.id}>{y.name}</option>)}
              </select>
              {lockBatchYear && (
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  Locked — this exam already has subjects. Remove them first to change the batch year.
                </div>
              )}
            </label>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: 1 }}>
                <div className="muted">From date *</div>
                <input type="date" value={form.fromDate} required
                       onChange={e => setField('fromDate', e.target.value)} />
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">To date *</div>
                <input type="date" value={form.toDate} required
                       onChange={e => setField('toDate', e.target.value)} />
              </label>
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              Subjects added to this exam must be scheduled within these dates.
            </div>
            {formError && <div className="error">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={busy}>
                {busy ? 'Saving…' : editId ? 'Save changes' : 'Create & add subjects'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </OrgLayout>
  );
}
